import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

const DebugConsole = styled.div`
  position: fixed;
  top: 10px;
  right: 10px;
  width: 300px;
  max-height: 400px;
  background: rgba(0, 0, 0, 0.9);
  color: #00ff00;
  font-family: 'Courier New', monospace;
  font-size: 11px;
  border: 1px solid #333;
  border-radius: 5px;
  z-index: 10000;
  overflow: hidden;
  display: ${props => props.visible ? 'block' : 'none'};
  
  @media (max-width: 768px) {
    width: calc(100vw - 20px);
    max-width: 350px;
    top: 5px;
    right: 5px;
    left: 5px;
    max-height: 300px;
  }
`;

const DebugHeader = styled.div`
  background: #333;
  color: white;
  padding: 5px 10px;
  font-weight: bold;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`;

const DebugContent = styled.div`
  padding: 10px;
  max-height: 350px;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    max-height: 250px;
  }
`;

const LogEntry = styled.div`
  margin-bottom: 5px;
  padding: 3px 0;
  border-bottom: 1px solid #222;
  word-wrap: break-word;
  
  &.error {
    color: #ff4444;
  }
  
  &.warning {
    color: #ffaa00;
  }
  
  &.info {
    color: #4444ff;
  }
  
  &.success {
    color: #44ff44;
  }
`;

const Timestamp = styled.span`
  color: #888;
  font-size: 10px;
`;

const ToggleButton = styled.button`
  background: #555;
  color: white;
  border: none;
  padding: 2px 8px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 10px;
  
  &:hover {
    background: #666;
  }
`;

const ClearButton = styled.button`
  background: #ff4444;
  color: white;
  border: none;
  padding: 2px 8px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 10px;
  margin-left: 5px;
  
  &:hover {
    background: #ff6666;
  }
`;

const OnScreenDebugConsole = () => {
  const [logs, setLogs] = useState([]);
  const [visible, setVisible] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const logRef = useRef(null);

  // Check if debug mode is enabled
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const debugMode = urlParams.get('debug') === 'true' || 
                     localStorage.getItem('debugMode') === 'true';
                     // Removed: process.env.NODE_ENV === 'development' to hide by default
    
    if (debugMode) {
      setVisible(true);
    }
    
    // Load persisted logs from localStorage
    const persistedLogs = localStorage.getItem('debugConsoleLogs');
    if (persistedLogs) {
      try {
        const parsedLogs = JSON.parse(persistedLogs);
        setLogs(parsedLogs);
        addLog('🔄 Restored logs from previous session', 'info');
      } catch (e) {
        console.error('Failed to parse persisted logs:', e);
      }
    }
  }, []);

  // Add log entry
  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const newLog = {
      id: Date.now(),
      message,
      type,
      timestamp
    };
    
    setLogs(prevLogs => {
      const updatedLogs = [...prevLogs, newLog];
      // Keep only last 50 logs to prevent memory issues
      const finalLogs = updatedLogs.slice(-50);
      
      // Persist logs to localStorage for crash recovery
      try {
        localStorage.setItem('debugConsoleLogs', JSON.stringify(finalLogs));
      } catch (e) {
        console.error('Failed to persist logs:', e);
      }
      
      return finalLogs;
    });
    
    // Auto-scroll to bottom
    setTimeout(() => {
      if (logRef.current) {
        logRef.current.scrollTop = logRef.current.scrollHeight;
      }
    }, 100);
  };

  // Override console methods to capture logs
  useEffect(() => {
    if (!visible) return;

    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;
    const originalConsoleLog = console.log;

    console.error = (...args) => {
      originalConsoleError.apply(console, args);
      const message = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ');
      addLog(message, 'error');
    };

    console.warn = (...args) => {
      originalConsoleWarn.apply(console, args);
      const message = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ');
      addLog(message, 'warning');
    };

    console.log = (...args) => {
      originalConsoleLog.apply(console, args);
      const message = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ');
      addLog(message, 'info');
    };

    // Capture unhandled errors
    const handleError = (event) => {
      addLog(`Unhandled Error: ${event.error?.message || event.message}`, 'error');
      if (event.error?.stack) {
        addLog(`Stack: ${event.error.stack}`, 'error');
      }
    };

    const handleUnhandledRejection = (event) => {
      addLog(`Unhandled Promise Rejection: ${event.reason}`, 'error');
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Add initial log
    addLog('🔍 Debug Console Active - Monitoring for HTTP2 errors...', 'success');
    
    // Check for potential crash recovery
    const lastCrashTime = localStorage.getItem('debugConsoleCrashTime');
    if (lastCrashTime) {
      const crashTime = new Date(lastCrashTime);
      const timeSinceCrash = Date.now() - crashTime.getTime();
      
      // If less than 5 minutes since potential crash, show recovery message
      if (timeSinceCrash < 5 * 60 * 1000) {
        addLog(`⚠️  Potential page crash detected at ${crashTime.toLocaleTimeString()}`, 'warning');
        addLog(`🔄 Time since crash: ${Math.round(timeSinceCrash / 1000)}s`, 'info');
        addLog(`💡 Check logs above for HTTP2 errors before crash`, 'info');
      }
      
      // Clear the crash time
      localStorage.removeItem('debugConsoleCrashTime');
    }
    
    // Set up crash detection - update timestamp every 10 seconds
    const crashDetectionInterval = setInterval(() => {
      localStorage.setItem('debugConsoleCrashTime', new Date().toISOString());
    }, 10000);
    
    // Clear crash detection on normal page unload
    const handleBeforeUnload = () => {
      localStorage.removeItem('debugConsoleCrashTime');
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
      console.log = originalConsoleLog;
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      clearInterval(crashDetectionInterval);
    };
  }, [visible]);

    // Listen for video errors and API requests
    useEffect(() => {
      if (!visible) return;

      // Track API requests to detect potential HTTP2 overload
      const originalFetch = window.fetch;
      const originalXHROpen = XMLHttpRequest.prototype.open;
      const activeRequests = new Map();
      let requestCount = 0;

      // Monitor fetch requests
      window.fetch = async (...args) => {
        const requestId = ++requestCount;
        const url = args[0];
        const startTime = Date.now();
        
        activeRequests.set(requestId, { url, startTime });
        addLog(`📡 API Request #${requestId}: ${url}`, 'info');
        
        if (activeRequests.size > 10) {
          addLog(`⚠️  High concurrent requests: ${activeRequests.size} active`, 'warning');
          addLog(`💡 This could cause HTTP2 protocol errors`, 'warning');
        }
        
        try {
          const response = await originalFetch(...args);
          const duration = Date.now() - startTime;
          activeRequests.delete(requestId);
          
          if (response.ok) {
            addLog(`✅ API Success #${requestId}: ${response.status} (${duration}ms)`, 'success');
          } else {
            addLog(`❌ API Error #${requestId}: ${response.status} ${response.statusText}`, 'error');
          }
          
          return response;
        } catch (error) {
          const duration = Date.now() - startTime;
          activeRequests.delete(requestId);
          addLog(`💥 API Failed #${requestId}: ${error.message} (${duration}ms)`, 'error');
          
          // Check for HTTP2 related errors
          if (error.message.includes('HTTP2') || error.message.includes('PROTOCOL_ERROR')) {
            addLog(`🔍 HTTP2 Protocol Error in API request detected!`, 'error');
            addLog(`📊 Active requests when error occurred: ${activeRequests.size}`, 'warning');
          }
          
          throw error;
        }
      };

      // Monitor XMLHttpRequest (axios uses this)
      XMLHttpRequest.prototype.open = function(method, url, ...args) {
        const requestId = ++requestCount;
        const startTime = Date.now();
        
        this._debugRequestId = requestId;
        this._debugStartTime = startTime;
        
        activeRequests.set(requestId, { url, startTime, method });
        addLog(`📡 XHR Request #${requestId}: ${method} ${url}`, 'info');
        
        if (activeRequests.size > 10) {
          addLog(`⚠️  High concurrent XHR requests: ${activeRequests.size} active`, 'warning');
        }
        
        // Add event listeners for this request
        this.addEventListener('load', () => {
          const duration = Date.now() - startTime;
          activeRequests.delete(requestId);
          addLog(`✅ XHR Success #${requestId}: ${this.status} (${duration}ms)`, 'success');
        });
        
        this.addEventListener('error', () => {
          const duration = Date.now() - startTime;
          activeRequests.delete(requestId);
          addLog(`❌ XHR Error #${requestId}: Network error (${duration}ms)`, 'error');
          addLog(`📊 Active requests when XHR error occurred: ${activeRequests.size}`, 'warning');
        });
        
        return originalXHROpen.call(this, method, url, ...args);
      };

      const handleVideoError = (event) => {
        const video = event.target;
        const error = video.error;
        
        if (error) {
          addLog(`🎥 Video Error Detected:`, 'error');
          addLog(`   Code: ${error.code}`, 'error');
          addLog(`   Message: ${error.message || 'No message'}`, 'error');
          addLog(`   Video URL: ${video.src}`, 'error');
          addLog(`📊 Active API requests: ${activeRequests.size}`, 'info');
          
          // Check for HTTP2 protocol errors
          if (error.code === 2) {
            addLog(`🔍 MEDIA_ERR_NETWORK detected - checking for HTTP2 issues...`, 'warning');
            
            if (video.src.includes('viewsubvideos-hwcbfwbmbpcjgthj.z02.azurefd.net')) {
              addLog(`⚠️  Azure CDN detected - likely HTTP2 protocol error`, 'error');
              addLog(`💡 Solution: Automatic retry with connection reset`, 'info');
              
              // Check if this correlates with high API activity
              if (activeRequests.size > 5) {
                addLog(`🔍 High API activity detected during video error!`, 'warning');
                addLog(`💡 Consider reducing concurrent video fetches`, 'info');
              }
            }
          }
          
          if (error.code === 5) {
            addLog(`🔒 MEDIA_ERR_ENCRYPTED detected - SAS token issue`, 'error');
          }
        }
      };

    // Add event listeners to all video elements
    const videos = document.querySelectorAll('video');
    videos.forEach(video => {
      video.addEventListener('error', handleVideoError);
    });

    // Observer for dynamically added videos
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.tagName === 'VIDEO') {
            node.addEventListener('error', handleVideoError);
          }
          if (node.querySelectorAll) {
            const newVideos = node.querySelectorAll('video');
            newVideos.forEach(video => {
              video.addEventListener('error', handleVideoError);
            });
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      // Restore original functions
      window.fetch = originalFetch;
      XMLHttpRequest.prototype.open = originalXHROpen;
      
      // Clean up video event listeners
      videos.forEach(video => {
        video.removeEventListener('error', handleVideoError);
      });
      observer.disconnect();
    };
  }, [visible]);

  const clearLogs = () => {
    setLogs([]);
  };

  const toggleVisibility = () => {
    setVisible(!visible);
    localStorage.setItem('debugMode', (!visible).toString());
  };

  const toggleMinimized = () => {
    setMinimized(!minimized);
  };

  // Hide debug console completely in production
  if (!visible) {
    return null;
  }

  return (
    <DebugConsole visible={visible}>
      <DebugHeader onClick={toggleMinimized}>
        <span>🔍 Debug Console ({logs.length})</span>
        <div>
          <ToggleButton onClick={toggleMinimized}>
            {minimized ? '▲' : '▼'}
          </ToggleButton>
          <ClearButton onClick={clearLogs}>Clear</ClearButton>
          <ToggleButton onClick={toggleVisibility} style={{ marginLeft: '5px' }}>
            ✕
          </ToggleButton>
        </div>
      </DebugHeader>
      
      {!minimized && (
        <DebugContent ref={logRef}>
          {logs.length === 0 ? (
            <LogEntry>No logs yet...</LogEntry>
          ) : (
            logs.map(log => (
              <LogEntry key={log.id} className={log.type}>
                <Timestamp>[{log.timestamp}]</Timestamp> {log.message}
              </LogEntry>
            ))
          )}
        </DebugContent>
      )}
    </DebugConsole>
  );
};

export default OnScreenDebugConsole;
